import { Component } from 'react';
import PropTypes from 'prop-types';
import { find, propEq } from 'ramda';
import classNames from 'classnames';
import Select, { components } from 'react-select';
import Creatable from 'react-select/creatable';
import s from './SelectFieldEditor.module.scss';
import Icon from '../icon/Icon.jsx';
import { selectFieldEditorStyles, FilledTriangleDropdownIndicator } from './selectStyles.jsx';

/**
 * The SelectFieldEditor is the React SelectComponent with added functionality. It checks whether or not the dropdown is creatable, can add icons, and
 * can enable or disable fields that are passed down.
 *
 * Static Labels and Buttons
 * @param {string} className: Class
 * @param {string} label: String that appears in the dropdown
 * @param {array} options: The label and the value for each selection
 * @param {boolean} creatable: Checks to see if field accepts the creation of new fields
 * @param {function} onValueSelected: Gets clicked value
 * @param {string} selectedValue: The clicked on value from the dropdown
 * @param {boolean} categoryIcons: The icons next to the fields
 * @param {boolean} disabled: Whether field is clickable or not
 *
 */

export default class SelectFieldEditor extends Component {
    static propTypes = {
        className: PropTypes.string,
        label: PropTypes.string,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.string,
                label: PropTypes.string,
            }),
        ),
        creatable: PropTypes.bool,
        onValueSelected: PropTypes.func,
        selectedValue: PropTypes.string,
        categoryIcons: PropTypes.bool,
        disabled: PropTypes.bool,
    };

    static defaultProps = {
        options: [
            { value: 'one', label: 'One' },
            { value: 'two', label: 'Two' },
        ],
        creatable: false,
        onValueSelected: () => {},
        disabled: false,
    };

    /**
     * Updates the props with the current option
     *
     */
    handleChange = selectedOption => {
        selectedOption ? this.props.onValueSelected(selectedOption.value) : this.props.onValueSelected('');
    };

    /**
     * Adds additional options to the dropdown, like creatability
     *
     */
    enhancedOptions = () => {
        const { selectedValue, options, creatable } = this.props;

        if (selectedValue && creatable && !find(propEq(selectedValue, 'value'), options)) {
            return [{ value: selectedValue, label: selectedValue }, ...options];
        }
        return options;
    };

    render() {
        const { label, creatable, categoryIcons, disabled, className } = this.props;
        const SelectComponent = creatable ? Creatable : Select;
        const options = this.enhancedOptions();
        const selectedOption = options.find(option => option.value === this.props.selectedValue);

        // Custom components for icon rendering (v5 way to replace optionRenderer/valueRenderer)
        const customComponents = categoryIcons
            ? {
                  Option: props => {
                      const iconUrl = props.data.badge || props.data.categoryIcon;
                      return (
                          <components.Option {...props}>
                              <div className={s.iconOption}>
                                  <Icon url={iconUrl} size={20} />
                                  <span className={s.iconLabel}>{props.data.label}</span>
                              </div>
                          </components.Option>
                      );
                  },
                  SingleValue: props => {
                      const iconUrl = props.data.badge || props.data.categoryIcon;
                      return (
                          <components.SingleValue {...props}>
                              <div className={s.iconOption}>
                                  <Icon url={iconUrl} size={20} />
                                  <span className={s.iconLabel}>{props.data.label}</span>
                              </div>
                          </components.SingleValue>
                      );
                  },
                  DropdownIndicator: FilledTriangleDropdownIndicator,
              }
            : {
                  DropdownIndicator: FilledTriangleDropdownIndicator,
              };

        return (
            <div
                className={classNames(s.selectFieldEditor, className, { [s.disabled]: disabled })}
                data-testid="select-field-editor"
            >
                {label && <div className={s.label}>{label.toUpperCase()}</div>}
                <SelectComponent
                    value={selectedOption}
                    isClearable={false}
                    onChange={this.handleChange}
                    options={options}
                    components={customComponents}
                    isDisabled={disabled}
                    styles={selectFieldEditorStyles}
                    classNamePrefix="react-select"
                />
            </div>
        );
    }
}
